import { Ouvrage, OuvrageType } from '../../1-bibliotheque-prix/data/ServicesData';

const STOPWORDS = new Set([
  'de', 'du', 'en', 'sur', 'le', 'la', 'les', 'un', 'une', 'des',
  'et', 'avec', 'pour', 'au', 'aux', 'à', 'par',
]);

const tokenize = (text: string): string[] => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove accents
    .split(/[\s/\-_,.']+/)
    .filter((t) => t.length > 1 && !STOPWORDS.has(t));
};

const tokenOverlapScore = (queryTokens: string[], targetTokens: string[]): number => {
  if (queryTokens.length === 0 || targetTokens.length === 0) return 0;

  let matches = 0;
  for (const qt of queryTokens) {
    for (const tt of targetTokens) {
      if (tt.includes(qt) || qt.includes(tt)) {
        matches++;
        break;
      }
    }
  }

  const precision = matches / queryTokens.length;
  const recall = matches / targetTokens.length;

  if (precision + recall === 0) return 0;
  // F1 score
  return (2 * precision * recall) / (precision + recall);
};

export interface FuzzyMatchResult {
  ouvrage: Ouvrage;
  score: number;
}

/**
 * Recherche fuzzy d'ouvrages par score de chevauchement de tokens.
 *
 * @param filterType  Filtre optionnel sur le type d'ouvrage :
 *   - 'F'   → ne retourne que les ouvrages fourniture nue (utilisé dans la subbar
 *     décomposition côté fourniture).
 *   - 'F+P' → ne retourne que les ouvrages fourni + posé (searchbar principale du
 *     chiffrage, ligne ventilée).
 *   - undefined → pas de filtre (comportement legacy).
 */
export const fuzzyMatchOuvrages = (
  query: string,
  ouvrages: Ouvrage[],
  filterType?: OuvrageType,
): FuzzyMatchResult[] => {
  const queryTokens = tokenize(query);
  if (queryTokens.length === 0) return [];

  const pool = filterType
    ? ouvrages.filter((o) => o.type === filterType)
    : ouvrages;

  return pool
    .map((ouvrage) => ({
      ouvrage,
      score: tokenOverlapScore(queryTokens, tokenize(ouvrage.intitule)),
    }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score);
};

export const bestMatch = (
  query: string,
  ouvrages: Ouvrage[],
  filterType?: OuvrageType,
): FuzzyMatchResult | null => {
  const results = fuzzyMatchOuvrages(query, ouvrages, filterType);
  if (results.length === 0 || results[0].score < 0.3) return null;
  return results[0];
};
